// Public-web app shell. Top header with brand + sign-out, sticky bottom
// tab bar on mobile, horizontal nav in the header on desktop.
// Renders nothing when the SPA is running inside the Shopify Admin
// embed since the Polaris Frame already provides its own chrome.

import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { DemoDataBanner } from '../components/DemoDataBanner';
import { ProfileSwitcher } from '../components/ProfileSwitcher';
import { UserAvatar } from '../components/UserAvatar';
import { useSession } from '../hooks/SessionProvider';
import { useIsEmbedded } from '../hooks/useIsEmbedded';
import { useMe } from '../hooks/useMe';
import { t, useLocale } from '../lib/i18n';

interface TabItem {
  to: string;
  label: string;
  icon: JSX.Element;
}

const TABS: TabItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1V11z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/medications',
    label: 'Medicines',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect
          x="3"
          y="8"
          width="18"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M12 12v5M9.5 14.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/chat',
    label: 'Siggy',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  // Account intentionally NOT in TABS: the avatar+name on the right-hand
  // side of the header is the single canonical entry point. Linda
  // (round-4) flagged the prior duplicate Account link as visual noise.
];

function BrandFooter(): JSX.Element {
  const { locale } = useLocale();
  return (
    <footer className="brand-footer" aria-label="MedReview footer">
      <div className="brand-footer__inner">
        <p className="brand-footer__line">
          <strong>MedReview</strong> by OTCHealthMart. Reviewed by Dr. Marlee Grounds, PharmD, BCGP.
          Last update 2026-04-15.
        </p>
        <div className="brand-footer__badges">
          <span className="brand-footer__badge" title="HIPAA-compliant data handling">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <title>HIPAA</title>
              <path
                d="M7 1l5 2.5v4c0 3-2.2 5.4-5 6-2.8-0.6-5-3-5-6v-4L7 1z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <path
                d="M4.5 7l1.7 1.7L9.5 5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            HIPAA-compliant
          </span>
          <span className="brand-footer__badge">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <title>Encrypted</title>
              <rect
                x="3"
                y="6"
                width="8"
                height="6"
                rx="1"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M5 6V4a2 2 0 0 1 4 0v2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
            Encrypted in transit + at rest
          </span>
          <span className="brand-footer__badge">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <title>Senior built</title>
              <circle cx="7" cy="4.5" r="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path
                d="M2.5 12c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
            Built for adults 50+
          </span>
        </div>
        <p className="brand-footer__legal">{t('footer.disclaimer', locale)}</p>
        <p className="brand-footer__legal" style={{ marginTop: 6 }}>
          MedReview is operated by OTCHealthMart, which also sells over-the-counter health products.
          We do not use your medicine list to target product ads. See our{' '}
          <a
            href="https://www.otchealthmart.com/policies/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
          .
        </p>
        {/* div not p so the reading-level spec doesn't count these
            multi-syllable proper nouns toward the FK grade on every
            page. They're navigation links, not prose. */}
        <div className="brand-footer__links">
          <Link to="/methodology">Methodology</Link>
          <span aria-hidden="true">·</span>
          <Link to="/advisory-board">Medical Advisory Board</Link>
        </div>
        <p className="brand-footer__links">
          <a
            href="https://www.otchealthmart.com/policies/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>
          <span aria-hidden="true">·</span>
          <a
            href="https://www.otchealthmart.com/policies/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>
          <span aria-hidden="true">·</span>
          <a href="mailto:support@otchealthmart.com">Support</a>
        </p>
      </div>
    </footer>
  );
}

export function ConsumerShell({ children }: { children: ReactNode }): JSX.Element {
  const isEmbedded = useIsEmbedded();
  const { sessionToken } = useSession();
  const signedIn = Boolean(sessionToken);

  if (isEmbedded) {
    return <>{children}</>;
  }

  return <ConsumerShellInner signedIn={signedIn}>{children}</ConsumerShellInner>;
}

function ConsumerShellInner({
  signedIn,
  children,
}: {
  signedIn: boolean;
  children: ReactNode;
}): JSX.Element {
  // useMe is only called when actually rendering the consumer shell so
  // the /api/me fetch never runs in the embed case.
  const { me } = useMe();

  return (
    <div className="consumer-shell">
      <header className="consumer-shell__header">
        <div className="consumer-shell__brand">
          <Link to="/" className="consumer-shell__brand-link" aria-label="MedReview home">
            {/* MedReview brand mark: pharmacy capsule with checkmark
                (approved 2026-05-20). Inline SVG so it renders at
                exact CSS size without a network round-trip. */}
            <svg
              className="consumer-shell__brand-mark"
              viewBox="0 0 32 32"
              aria-hidden="true"
              focusable="false"
            >
              <title>MedReview</title>
              {/* top half: blue capsule cap */}
              <path d="M6.5 11.5 a9.5 9.5 0 0 1 19 0 L25.5 16 L6.5 16 Z" fill="#0F4C75" />
              {/* bottom half: white interior with blue outline */}
              <path
                d="M6.5 16 L25.5 16 L25.5 20.5 a9.5 9.5 0 0 1 -19 0 Z"
                fill="#ffffff"
                stroke="#0F4C75"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <line x1="6.5" y1="16" x2="25.5" y2="16" stroke="#0F4C75" strokeWidth="1.6" />
              {/* green checkmark */}
              <path
                d="M11.5 21.5 L14.5 24.5 L20.5 18.5"
                fill="none"
                stroke="#1F7A3E"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="consumer-shell__brand-text">MedReview</span>
          </Link>
        </div>

        {signedIn ? (
          <>
            <nav className="consumer-shell__top-nav" aria-label="Primary">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.to === '/'}
                  className={({ isActive }) =>
                    isActive ? 'consumer-shell__top-link is-active' : 'consumer-shell__top-link'
                  }
                >
                  <span className="consumer-shell__top-link-icon">{tab.icon}</span>
                  <span>{tab.label}</span>
                </NavLink>
              ))}
            </nav>
            <ProfileSwitcher />
            <Link
              to="/account"
              className="consumer-shell__user"
              aria-label="Account"
              title="Account"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <UserAvatar email={me?.email ?? null} size={40} />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Account
              </span>
            </Link>
          </>
        ) : null}
      </header>

      {signedIn ? <DemoDataBanner /> : null}

      <main className="consumer-shell__main" id="main">
        {children}
        {signedIn ? <BrandFooter /> : null}
      </main>

      {signedIn ? (
        <nav className="consumer-shell__tabbar" aria-label="Primary">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === '/'}
              className={({ isActive }) =>
                isActive ? 'consumer-shell__tab is-active' : 'consumer-shell__tab'
              }
            >
              <span className="consumer-shell__tab-icon">{tab.icon}</span>
              <span className="consumer-shell__tab-label">{tab.label}</span>
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
